import { TableFilterConfig } from '@/components/data-table/filters-v2';
import i18n from '@/core/i18n';
import { EntityAvatar } from '@/modules/common';
import { getTimeEntryStatusOptions } from '@/modules/common/components/molecules/badge/status-configs/time-entry-status-config';
import { CustomAttribute } from '@/modules/common/types/custom-attribute';
import { buildCustomAttributeFilterConfigsV2 } from '@/modules/common/utils/custom-attribute-filters';
import { getShortProjects } from '@/modules/projects/api/short-projects.api';
import { renderProjectFilterLabel } from '@/modules/projects/helpers/project-dropdown-option';
import { loadDepartmentsForFilter } from '@/modules/team/api/team-filter-options.api';
import { getShortTeamMembers } from '@/modules/team/api/team.api';

import { TimeEntryStatus } from '../types/time-tracking-approvals';

export const getApprovalsFiltersConfig = (
  customAttributes?: CustomAttribute[]
): TableFilterConfig[] => [
  {
    type: 'search',
    name: 'searchString',
    label: i18n.t('authenticated.timeTracking:approvals.filters.searchPlaceholder'),
    placeholder: i18n.t('authenticated.timeTracking:approvals.filters.searchPlaceholder'),
    saveable: false,
  },
  {
    type: 'dynamic-autocomplete',
    name: 'departmentIds',
    label: i18n.t('authenticated.timeTracking:approvals.filters.department'),
    withSearch: true,
    loadOptions: loadDepartmentsForFilter,
    order: 1,
  },
  {
    type: 'dynamic-autocomplete',
    name: 'projectIds',
    label: i18n.t('authenticated.timeTracking:approvals.filters.project'),
    withSearch: true,
    loadOptions: async params => {
      return getShortProjects({
        filter: { searchString: params.searchString },
        pagination: { cursor: params.cursor, pageSize: params.pageSize },
      });
    },
    renderLabel: renderProjectFilterLabel,
    order: 2,
  },
  {
    type: 'dynamic-autocomplete',
    name: 'memberIds',
    label: i18n.t('authenticated.timeTracking:approvals.filters.member'),
    withSearch: true,
    loadOptions: async params => {
      return getShortTeamMembers({
        filter: { searchString: params.searchString },
        pagination: { cursor: params.cursor, pageSize: params.pageSize },
      });
    },
    renderLabel: (value, option) => (
      <div className="flex items-center gap-2">
        <EntityAvatar image={option.avatar} name={value} size="xs" shape="circle" />
        <span className="text-sm">{value}</span>
      </div>
    ),
    order: 3,
  },
  {
    type: 'autocomplete',
    name: 'statuses',
    label: i18n.t('authenticated.timeTracking:approvals.filters.status'),
    // REJECTED is intentionally omitted — there is no rejected status on the
    // backend or in the product.
    options: getTimeEntryStatusOptions([TimeEntryStatus.SUBMITTED, TimeEntryStatus.APPROVED]),
    displayDot: true,
    withSearch: false,
    order: 4,
  },
  ...(customAttributes
    ? buildCustomAttributeFilterConfigsV2(customAttributes, 5, {
        noValueOptionLabel: i18n.t('common:tableFilters.noAttributeValue'),
      })
    : []),
];
